import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

interface ArrivalVideoProps {
  open: boolean;
  onComplete: () => void;
}

export default function ArrivalVideo({ open, onComplete }: ArrivalVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setVideoLoaded(false);
    }
  }, [open]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(!muted);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        bgcolor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <video
        ref={videoRef}
        src="/video/Thru-Simulator.mp4"
        autoPlay
        playsInline
        muted
        onEnded={onComplete}
        onCanPlay={() => setVideoLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: videoLoaded ? 1 : 0,
          transition: 'opacity 0.5s',
        }}
      >
        <track kind="captions" srcLang="fr" label="Français" />
      </video>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
        }}
      />

      {!videoLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: 'white' }} />
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
            Detection d&apos;un véhicule en approche...
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 32,
          left: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MicIcon sx={{ color: '#2563EB', fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
          AI-LYN
        </Typography>
      </Box>

      <IconButton
        onClick={toggleMute}
        sx={{
          position: 'absolute',
          top: 32,
          right: 32,
          bgcolor: 'rgba(0,0,0,0.4)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}
      >
        {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          bottom: 48,
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'white',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
            mb: 1,
          }}
        >
          Véhicule en approche...
        </Typography>
      </Box>

      <Button
        variant="contained"
        onClick={onComplete}
        sx={{
          position: 'absolute',
          bottom: 48,
          right: 48,
          bgcolor: 'rgba(255,255,255,0.15)',
          backdropFilter: 'blur(8px)',
          color: 'white',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: 3,
          px: 3,
          py: 1.5,
          border: '1px solid rgba(255,255,255,0.2)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
        }}
      >
        Passer
      </Button>
    </Box>
  );
}
